import { ISaleOrderDetail } from 'features/salesOrder/types';

export interface ServiceFinancialSnapshot {
	totalCost: number;
	totalSaleAmount: number;
	margin: number;
	markupPercentage?: number;
}

export interface ServiceSaveImpact {
	service: {
		before: ServiceFinancialSnapshot;
		after: ServiceFinancialSnapshot;
		deltaCost: number;
		deltaSale: number;
		deltaMargin: number;
	};
	saleOrder: {
		currency: string;
		before: { totalCost: number; saleAmount: number; grossProfit: number };
		after: { totalCost: number; saleAmount: number; grossProfit: number };
		deltaMargin: number;
	};
}

const toNumber = (value: unknown): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

export const extractServiceFinancials = (service: Record<string, unknown>): ServiceFinancialSnapshot => {
	const totalCost = toNumber(service.totalCost);
	const totalSaleAmount = toNumber(service.totalSaleAmount);

	return {
		totalCost,
		totalSaleAmount,
		margin: totalSaleAmount - totalCost,
		markupPercentage:
			service.markupPercentage !== undefined && service.markupPercentage !== null
				? toNumber(service.markupPercentage)
				: undefined,
	};
};

export const computeServiceSaveImpact = (
	salesOrder: ISaleOrderDetail,
	originalService: Record<string, unknown>,
	updatedService: Record<string, unknown>,
): ServiceSaveImpact => {
	const before = extractServiceFinancials(originalService);
	const after = extractServiceFinancials(updatedService);

	const soBeforeCost = toNumber(salesOrder.totalCost);
	const soBeforeSale = toNumber(salesOrder.saleAmount);
	const soBeforeMargin = toNumber(salesOrder.grossProfit);

	const soAfterCost = soBeforeCost - before.totalCost + after.totalCost;
	const soAfterSale = soBeforeSale - before.totalSaleAmount + after.totalSaleAmount;
	const soAfterMargin = soAfterSale - soAfterCost;

	return {
		service: {
			before,
			after,
			deltaCost: after.totalCost - before.totalCost,
			deltaSale: after.totalSaleAmount - before.totalSaleAmount,
			deltaMargin: after.margin - before.margin,
		},
		saleOrder: {
			currency: salesOrder.currency,
			before: {
				totalCost: soBeforeCost,
				saleAmount: soBeforeSale,
				grossProfit: soBeforeMargin,
			},
			after: {
				totalCost: soAfterCost,
				saleAmount: soAfterSale,
				grossProfit: soAfterMargin,
			},
			deltaMargin: soAfterMargin - soBeforeMargin,
		},
	};
};

export const formatMoney = (amount: number, currency: string): string =>
	`${currency} ${amount.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatDelta = (amount: number, currency: string): string => {
	if (amount === 0) return '—';
	const sign = amount > 0 ? '+' : '';
	return `${sign}${formatMoney(amount, currency)}`;
};
